import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    value: "+234 802 699 4903",
    link: "tel:+2348026994903",
    description: "Call us directly"
  },
  {
    icon: Mail,
    title: "Email",
    value: "info@afrigrowfarm.com",
    link: "mailto:info@afrigrowfarm.com",
    description: "Send us an email"
  },
  {
    icon: MapPin,
    title: "Location",
    value: "Igbodu Epe, Lagos State, Nigeria",
    link: "https://maps.google.com/?q=JWX7+J8+Igbodu,+Nigeria",
    description: "Plus Code: JWX7+J8"
  },
  {
    icon: Clock,
    title: "Business Hours",
    value: "Mon - Sat: 8AM - 5PM",
    link: null,
    description: "Sunday: Closed"
  }
];

const subjects = [
  { value: "general", label: "General Inquiry" },
  { value: "products", label: "Product Order" },
  { value: "bulk", label: "Bulk Order Request" },
  { value: "visit", label: "Farm Visit" },
  { value: "partnership", label: "Business Partnership" },
  { value: "other", label: "Other" }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectChange = (value) => {
    setFormData(prev => ({ ...prev, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contacts`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              Get In Touch
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Contact AfriGrow Farm
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              Have questions about our products or services? Want to place an order or schedule 
              a farm visit? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white relative -mt-8" data-testid="contact-info-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((item, index) => (
              <div 
                key={item.title}
                className="bg-stone-50 p-6 rounded-2xl hover:bg-stone-100 transition-colors group"
                data-testid={`contact-info-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-600 transition-colors">
                    <item.icon className="h-6 w-6 text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-stone-900">
                    {item.title}
                  </h3>
                </div>
                {item.link ? (
                  <a 
                    href={item.link}
                    target={item.title === "Location" ? "_blank" : undefined}
                    rel={item.title === "Location" ? "noopener noreferrer" : undefined}
                    className="text-green-600 font-medium hover:text-green-700 transition-colors block mb-1"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="text-stone-900 font-medium mb-1">{item.value}</p>
                )}
                <p className="text-stone-500 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-stone-50" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm">
              <h2 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
                Send Us a Message
              </h2>
              <p className="text-stone-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {submitted ? (
                <div className="text-center py-12" data-testid="form-success">
                  <div className="inline-flex p-4 bg-green-100 rounded-full mb-6">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-stone-600 mb-6">
                    Thank you for contacting us. We'll respond to your inquiry soon.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-stone-700">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="bg-stone-50 border-stone-200 focus:border-green-600 focus:ring-green-600 rounded-xl"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-stone-700">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-stone-50 border-stone-200 focus:border-green-600 focus:ring-green-600 rounded-xl"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+234 XXX XXX XXXX"
                        className="bg-stone-50 border-stone-200 focus:border-green-600 focus:ring-green-600 rounded-xl"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-stone-700">Subject *</Label>
                      <Select value={formData.subject} onValueChange={handleSubjectChange} required>
                        <SelectTrigger 
                          className="bg-stone-50 border-stone-200 focus:border-green-600 focus:ring-green-600 rounded-xl"
                          data-testid="select-subject"
                        >
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((subject) => (
                            <SelectItem 
                              key={subject.value} 
                              value={subject.value}
                              data-testid={`subject-${subject.value}`}
                            >
                              {subject.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-stone-700">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      className="bg-stone-50 border-stone-200 focus:border-green-600 focus:ring-green-600 rounded-xl resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-6 text-lg font-medium"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message <Send className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              {/* Map */}
              <div className="bg-white p-4 rounded-2xl shadow-sm overflow-hidden" data-testid="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.952912260219!2d3.600!3d6.524!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMzEnMjYuNCJOIDPCsDM2JzAwLjAiRQ!5e0!3m2!1sen!2sng!4v1609459200000!5m2!1sen!2sng"
                  width="100%"
                  height="350"
                  style={{ border: 0, borderRadius: '1rem' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="AfriGrow Farm Location"
                />
              </div>

              {/* Quick Contact */}
              <div className="bg-green-600 p-8 rounded-2xl text-white">
                <h3 className="font-heading text-2xl font-semibold mb-4">
                  Prefer to Call?
                </h3>
                <p className="text-green-100 mb-6">
                  For urgent inquiries or immediate assistance, give us a call. 
                  We're available during business hours.
                </p>
                <a 
                  href="tel:+2348026994903"
                  className="inline-flex items-center gap-3 bg-white text-green-700 px-6 py-3 rounded-full font-medium hover:bg-stone-100 transition-colors"
                  data-testid="call-now-btn"
                >
                  <Phone className="h-5 w-5" />
                  +234 802 699 4903
                </a>
              </div>

              {/* WhatsApp */}
              <div className="bg-stone-900 p-8 rounded-2xl text-white">
                <h3 className="font-heading text-2xl font-semibold mb-4">
                  Chat on WhatsApp
                </h3>
                <p className="text-stone-400 mb-6">
                  Send us a message on WhatsApp for quick responses and easy communication.
                </p>
                <a 
                  href="https://wa.me/2348026994903"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-green-500 text-white px-6 py-3 rounded-full font-medium hover:bg-green-600 transition-colors"
                  data-testid="whatsapp-btn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Chat with Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-accent text-sm uppercase tracking-widest text-terracotta mb-4 block">
              Common Questions
            </span>
            <h2 className="font-heading text-4xl font-semibold text-stone-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                q: "Do you deliver outside Lagos?",
                a: "Currently, we deliver within Lagos. For orders outside Lagos, please contact us to discuss logistics."
              },
              {
                q: "What is the minimum order quantity?",
                a: "Minimum orders vary by product. Contact us for specific details on the products you're interested in."
              },
              {
                q: "Can I visit the farm?",
                a: "Yes! We welcome farm visits. Please schedule an appointment through our contact form or by phone."
              },
              {
                q: "How do I place a bulk order?",
                a: "For bulk orders, contact us via phone or fill out the contact form with details of your requirements for a custom quote."
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-stone-50 p-6 rounded-2xl"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">
                  {faq.q}
                </h3>
                <p className="text-stone-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
